import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent } from '@ag-grid-enterprise/all-modules'
import UnitsModalBasicForm from '@/modules/configurationModule/detail/accounting/billable/units/components/modal-basic-form/configuration-accounting-billable-units-modal-basic-form.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import { ConfigurationAccountingUnitGridState, ConfigurationLocalAccountingUnitGridSidepanelComboElements, initialConfigurationAccountingUnitGridState } from '@/modules/configurationModule/detail/accounting/billable/units/components/grid/configuration-accounting-billable-units-grid.model'
import { ConfigurationAccountingUnitCreateRequest, ConfigurationAccountingUnitPatchInBatchRequest } from '@/modules/configurationModule/detail/accounting/billable/units/services/configuration-accounting-billable-unit/configuration-accounting-billable-unit.dto'
import { EntityIdentifierWithErrorResponse, EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { StrictNullable } from '@/shared/types/app.types'
import ConfigurationListSidepanel from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import ConfigurationListSidepanelComp from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import { billableRateService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.service'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { GridUtils } from '@/shared/util/grid-utils'
import { AccountingUnitResponse } from '@/api/services/billing/common-accounting-unit/billing-common-accounting-unit.dto'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        UnitsModalBasicForm,
        ConfigurationListSidepanel
    }
})
export default class ConfigurationAccountingBillableUnitsGrid extends BaseCrudServerSideComponent<AccountingUnitResponse> implements BaseCrud {
    @Ref() readonly unitsModalBasicFormWrapper!: typeof ModalWrapper
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: ConfigurationAccountingUnitGridState

    get canUpdate(): boolean {
        return this.state.gridApi?.getSelectedRows().length === 1
    }

    get canDelete(): boolean {
        return !!this.state.gridApi?.getSelectedRows().length
    }

    constructor() {
        super()
        this.state = initialConfigurationAccountingUnitGridState()
    }

    /// SERVICES CALLS ///

    private async _createAccountingUnit(createRequest: ConfigurationAccountingUnitCreateRequest): Promise<StrictNullable<AccountingUnitResponse>> {
        const response = await this.$_toCallUnique(this.state.service.create(createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
        }
        return response
    }

    private async _patchAccountingUnits(batchRequests: ConfigurationAccountingUnitPatchInBatchRequest[]): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteAccountingUnits(requestDelete: EntityIdentifiersRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, accountingUnits: AccountingUnitResponse[]): Promise<void> {
        if (accountingUnits?.length) {
            const batchRequests =
                accountingUnits
                    .filter(accountingUnit => accountingUnit.active !== active)
                    .map(accountingUnit => ({
                        id: accountingUnit.id,
                        active
                    }))
            if (await this._patchAccountingUnits(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationAccountingUnitCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createAccountingUnit(createRequest)) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize = this.state?.selectedRows?.length
        const labelGrid = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async patchOperation(batchRequest: ConfigurationAccountingUnitPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAccountingUnits([batchRequest])) {
                this.reloadData()
            }
        }
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                this.configurationListSidepanel.hide()
                if (await this._deleteAccountingUnits(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(accountingUnit: AccountingUnitResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        this.configurationListSidepanel.hide()
        if (accountingUnit) {
            this.showModal(this.$tc('configuration.actions.update'), accountingUnit, this.unitsModalBasicFormWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(accountingUnit => accountingUnit.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.configurationListSidepanel.hide()
        this.showModal(this.$tc('configuration.actions.add'), null, this.unitsModalBasicFormWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }

    onRowClicked(event: RowClickedEvent): void {
        const unitSelected: AccountingUnitResponse = event.node.data
        this.configurationListSidepanel.show({
            title: unitSelected.name,
            entities: [
                {
                    id: ConfigurationLocalAccountingUnitGridSidepanelComboElements.RATES,
                    entityKey: 'configuration.sections.rate'
                }
            ],
            obtainEntityList: (): Promise<string[]> => this._getAssignedRates(unitSelected)
        })
    }

    private async _getAssignedRates(unitSelected: AccountingUnitResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'accountingUnit.id': GridUtils.getNumberFilterModel('equals', unitSelected?.id)
        }

        const laboratoryId = this.$store.getters.labId
        const response =
            await this.$_toCallSecure(
                billableRateService.paginator(
                    filterModel,
                    params,
                    laboratoryId))
        return response?.content.map(item => item.name) ?? []
    }
}
